import type { LoanPlannerSnapshot } from '../core/storage/loanPlannerStorage';
import type { SampleLiability } from '../types/financial';
import type { ExtraPayment } from './loanAmortization';
import { estimateRemainingTermMonths, isValidMonthKey } from './loanAmortization';

export const DEFAULT_LOAN_TERM_YEARS = 30;

/** Canonical id for the primary home-loan planner (mobile ↔ web sync). */
export const HOME_LOAN_PLANNER_ID = 'home_loan';

/** UI placeholder when no real loan has been saved yet — never persist this as truth. */
export const BLANK_LOAN_PRINCIPAL = 300_000;
export const BLANK_LOAN_RATE_PCT = 6.5;

/** Legacy ids that may already exist in local/remote stores. */
export function loanPlannerLookupIds(mortgageId?: string | null): string[] {
  const ids = [
    HOME_LOAN_PLANNER_ID,
    'default',
    'liab_mortgage',
    'user_mortgage_default',
  ];
  if (mortgageId && !ids.includes(mortgageId)) ids.unshift(mortgageId);
  return ids;
}

/** True when a snapshot looks like the unsaved Loan-tab placeholder (not real user data). */
export function isBlankLoanPlannerSnapshot(
  snapshot: Pick<LoanPlannerSnapshot, 'principal' | 'interestRatePct' | 'termYears'> & {
    extras?: ExtraPayment[];
    recurringExtra?: number;
    startMonth?: string | null;
  },
): boolean {
  const extrasEmpty = !snapshot.extras?.length;
  const recurringZero = !((snapshot.recurringExtra ?? 0) > 0);
  const noStart = !snapshot.startMonth;
  return (
    snapshot.principal === BLANK_LOAN_PRINCIPAL &&
    snapshot.interestRatePct === BLANK_LOAN_RATE_PCT &&
    snapshot.termYears === DEFAULT_LOAN_TERM_YEARS &&
    extrasEmpty &&
    recurringZero &&
    noStart
  );
}

export type LoanPlannerInputs = {
  principal: number;
  interestRatePct: number;
  termYears: number;
  label: string;
  extras: ExtraPayment[];
  recurringExtra: number;
  startMonth: string | null;
  /** True when values come from a saved snapshot or household mortgage (not blank defaults). */
  hasSeed: boolean;
};

export function seedFromMortgage(mortgage: {
  balance: number;
  interestRatePct?: number;
  monthlyPayment?: number;
  label: string;
}): Pick<LoanPlannerInputs, 'principal' | 'interestRatePct' | 'termYears' | 'label'> {
  const principal = Math.max(0, mortgage.balance);
  const interestRatePct = mortgage.interestRatePct ?? 6.5;
  let termMonths: number | null = null;

  if (mortgage.monthlyPayment != null && mortgage.monthlyPayment > 0) {
    termMonths = estimateRemainingTermMonths(
      principal,
      interestRatePct,
      mortgage.monthlyPayment,
    );
  }

  const termYears = termMonths
    ? Math.max(1, Math.round(termMonths / 12))
    : DEFAULT_LOAN_TERM_YEARS;

  return {
    principal,
    interestRatePct,
    termYears,
    label: mortgage.label,
  };
}

/** Resolve planner inputs from remote/local snapshot, mortgage seed, or blank defaults. */
export function resolveLoanPlannerInputs(
  loanId: string,
  mortgage: SampleLiability | null | undefined,
  snapshot: LoanPlannerSnapshot | null,
): LoanPlannerInputs {
  const usableSnapshot =
    snapshot &&
    (snapshot.loanId === loanId || loanPlannerLookupIds().includes(snapshot.loanId)) &&
    !isBlankLoanPlannerSnapshot(snapshot)
      ? snapshot
      : null;

  if (usableSnapshot) {
    return {
      principal: usableSnapshot.principal,
      interestRatePct: usableSnapshot.interestRatePct,
      termYears: usableSnapshot.termYears,
      label: mortgage?.label ?? 'Home loan',
      extras: usableSnapshot.extras ?? [],
      recurringExtra: usableSnapshot.recurringExtra ?? 0,
      startMonth: isValidMonthKey(usableSnapshot.startMonth)
        ? usableSnapshot.startMonth
        : null,
      hasSeed: true,
    };
  }
  if (mortgage && mortgage.balance > 0) {
    const seed = seedFromMortgage(mortgage);
    // If household only has the buggy blank $300k liability, don't treat it as seeded truth.
    const looksBlank =
      mortgage.balance === BLANK_LOAN_PRINCIPAL &&
      (mortgage.interestRatePct == null || mortgage.interestRatePct === BLANK_LOAN_RATE_PCT);
    return { ...seed, extras: [], recurringExtra: 0, startMonth: null, hasSeed: !looksBlank };
  }
  return {
    principal: BLANK_LOAN_PRINCIPAL,
    interestRatePct: BLANK_LOAN_RATE_PCT,
    termYears: DEFAULT_LOAN_TERM_YEARS,
    label: 'Home loan',
    extras: [],
    recurringExtra: 0,
    startMonth: null,
    hasSeed: false,
  };
}
